/**
 * Driver-portal API client (C5).
 *
 * All /v1/driver-portal/me/* endpoints are served by service-delivery and
 * are driver-scoped (each resolves the caller's driver record from the JWT sub).
 *
 * The `driverIdOverride` param is accepted by every endpoint — useful for
 * platform-admin e2e tests where the JWT sub is a synthetic string rather
 * than a real keycloakSub.
 *
 * Legacy wrappers for the overview page (listMyRoutes / listMyActiveRoute)
 * still hit the existing /v1/routes endpoint because that surface already
 * returns stops in the response — the driver-portal schedule endpoint
 * provides the same data via /v1/driver-portal/me/schedule.
 */
import { env } from '../env';
import { request } from './client';
import {
  type DeliveryRoute,
  type Driver,
  type ListPodsResponse,
  type ListRoutesResponse,
  getDriver,
  listPodQueue,
  listRoutes,
} from './delivery';

export interface DriverPortalQuery {
  readonly driverId: string;
}

// ---------------------------------------------------------------------------
// Legacy wrappers (overview page — still uses /v1/routes directly)
// ---------------------------------------------------------------------------

export async function getMyDriver({ driverId }: DriverPortalQuery): Promise<Driver> {
  return getDriver(driverId);
}

export async function listMyRoutes(
  { driverId }: DriverPortalQuery,
  pageSize = 20,
): Promise<ListRoutesResponse> {
  return listRoutes({ driverId, pageSize });
}

export async function listMyActiveRoute(
  query: DriverPortalQuery,
): Promise<DeliveryRoute | null> {
  const list = await listMyRoutes(query, 5);
  const active = list.items.find(
    (r) => r.status === 'DISPATCHED' || r.status === 'IN_PROGRESS',
  );
  return active ?? null;
}

export async function listMyPodHistory(): Promise<ListPodsResponse> {
  return listPodQueue({ pageSize: 20 });
}

// ---------------------------------------------------------------------------
// New /v1/driver-portal/me/* endpoints (C5)
// ---------------------------------------------------------------------------

const base = (): string => env.serviceDeliveryBaseUrl;

// Profile
export interface DriverSelfProfile {
  id: string;
  driverNumber: string;
  fullName: string;
  primaryPhone: string;
  licenseNumber: string | null;
  licenseExpiresAt: string | null;
  assignedVehicleId: string | null;
  status: string;
  hiredAt: string;
}

export async function getDriverPortalMe(driverIdOverride?: string): Promise<DriverSelfProfile> {
  return request<DriverSelfProfile>(base(), '/v1/driver-portal/me', {
    method: 'GET',
    query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
  });
}

// Schedule
export interface DriverPortalScheduleResponse {
  items: {
    id: string;
    routeCode: string;
    status: string;
    stopCount: number;
    stops: {
      id: string;
      sequence: number;
      kind: string;
      status: string;
      mailItemId: string | null;
      contactName: string | null;
      address: unknown;
    }[];
  }[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  total: number;
}

export async function getDriverPortalSchedule(driverIdOverride?: string): Promise<DriverPortalScheduleResponse> {
  return request<DriverPortalScheduleResponse>(base(), '/v1/driver-portal/me/schedule', {
    method: 'GET',
    query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
  });
}

// Deliveries
export interface DriverPortalDeliveriesResponse {
  items: {
    id: string;
    mailItemId: string;
    recipientName: string;
    reviewStatus: string;
    capturedAt: string;
  }[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  total: number;
}

export async function getDriverPortalDeliveries(driverIdOverride?: string): Promise<DriverPortalDeliveriesResponse> {
  return request<DriverPortalDeliveriesResponse>(base(), '/v1/driver-portal/me/deliveries', {
    method: 'GET',
    query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
  });
}

// Scorecard
export interface ScorecardResponse {
  stopsAttempted: number;
  stopsDelivered: number;
  onTimeRate: number | null;
  firstAttemptSuccessRate: number | null;
  periodDays: number;
  generatedAt: string;
}

export async function getDriverPortalScorecard(driverIdOverride?: string): Promise<ScorecardResponse> {
  return request<ScorecardResponse>(base(), '/v1/driver-portal/me/scorecard', {
    method: 'GET',
    query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
  });
}

// Earnings — real values now (driver_earnings_config-driven). Money fields are
// DECIMAL strings in NGN minor units (kobo). Formatters live in lib/format.
export interface EarningsBreakdownLine {
  label: string;
  amountMinor: string;
  qty: number;
  rateMinor: string;
}

export interface EarningsResponse {
  driverId: string;
  periodStart: string;
  periodEnd: string;
  grossMinorUnits: string;
  netMinorUnits: string;
  currency: string;
  stopsDelivered: number;
  stopsAttempted: number;
  onTimePercent: number | null;
  hoursWorked: number;
  bonusMinorUnits: string;
  perStopMinor: string;
  baseMinor: string;
  breakdown: EarningsBreakdownLine[];
  configSource: 'OPERATOR_OVERRIDE' | 'DEFAULT';
}

export async function getDriverPortalEarnings(driverIdOverride?: string): Promise<EarningsResponse> {
  return request<EarningsResponse>(base(), '/v1/driver-portal/me/earnings', {
    method: 'GET',
    query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
  });
}

// Notifications — real, backed by driver_notification table.
export interface DriverNotification {
  id: string;
  category: string;
  subject: string;
  body: string;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsResponse {
  items: DriverNotification[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  total: number;
  unread: number;
}

export async function getDriverPortalNotifications(driverIdOverride?: string): Promise<NotificationsResponse> {
  return request<NotificationsResponse>(base(), '/v1/driver-portal/me/notifications', {
    method: 'GET',
    query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
  });
}

export async function markDriverNotificationRead(
  notificationId: string,
  driverIdOverride?: string,
): Promise<{ id: string; readAt: string }> {
  return request<{ id: string; readAt: string }>(
    base(),
    `/v1/driver-portal/me/notifications/${encodeURIComponent(notificationId)}/read`,
    {
      method: 'POST',
      body: {},
      query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
    },
  );
}

/** Admin/dispatcher push — POST /v1/driver-portal/notifications. */
export async function pushDriverNotification(body: {
  driverId: string;
  category:
    | 'SAFETY'
    | 'REASSIGNMENT'
    | 'ADVISORY'
    | 'WEATHER'
    | 'PAYROLL'
    | 'INCENTIVE'
    | 'TRAINING'
    | 'ATTESTATION'
    | 'RATING'
    | 'SYSTEM';
  subject: string;
  body: string;
  link?: string;
}): Promise<DriverNotification & { driverId: string }> {
  return request<DriverNotification & { driverId: string }>(
    base(),
    '/v1/driver-portal/notifications',
    { method: 'POST', body },
  );
}

// Profile updates
export interface UpdateProfileRequest {
  primaryPhone?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelation?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankAccountName?: string;
  notifySafety?: boolean;
  notifyPayroll?: boolean;
  notifyAdvisory?: boolean;
}

export interface FullDriverProfile {
  id: string;
  driverNumber: string;
  fullName: string;
  primaryPhone: string;
  licenseNumber: string | null;
  licenseExpiresAt: string | null;
  assignedVehicleId: string | null;
  status: string;
  hiredAt: string;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelation: string | null;
  bankName: string | null;
  bankAccountNumber: string | null;
  bankAccountName: string | null;
  bankUpdatedAt: string | null;
  notifySafety: boolean;
  notifyPayroll: boolean;
  notifyAdvisory: boolean;
}

export async function updateDriverProfile(
  body: UpdateProfileRequest,
  driverIdOverride?: string,
): Promise<FullDriverProfile> {
  return request<FullDriverProfile>(base(), '/v1/driver-portal/me/profile', {
    method: 'PATCH',
    body,
    query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
  });
}

// Active shift
export interface ActiveShiftResponse {
  shift: {
    id: string;
    driverId: string;
    vehicleId: string | null;
    operatorId: number;
    startedAt: string;
    status: string;
    stopsAttempted: number;
    stopsDelivered: number;
    cashCollected: string;
  } | null;
  lastGpsAt: string | null;
  lastLatitude: string | null;
  lastLongitude: string | null;
  distanceMetersLast24h: number;
}

export async function getDriverPortalActiveShift(driverIdOverride?: string): Promise<ActiveShiftResponse> {
  return request<ActiveShiftResponse>(base(), '/v1/driver-portal/me/active-shift', {
    method: 'GET',
    query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
  });
}

// Safety check-in
export interface SafetyCheckinRequest {
  vehicleConditionOk?: boolean;
  fitToDriveOk?: boolean;
  notes?: string;
}

export interface SafetyCheckinResponse {
  id: string;
  driverId: string;
  checkInAt: string;
  vehicleConditionOk: boolean;
  fitToDriveOk: boolean;
  notes: string | null;
}

export async function postSafetyCheckin(
  body: SafetyCheckinRequest,
  driverIdOverride?: string,
): Promise<SafetyCheckinResponse> {
  return request<SafetyCheckinResponse>(base(), '/v1/driver-portal/me/safety-checkin', {
    method: 'POST',
    body,
    query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
  });
}

// Safety stats
export interface SafetyStatsResponse {
  sosTriggers90d: number;
  accidentFreeDays: number | null;
  openCases: number;
  attestationsDue: number;
  generatedAt: string;
}

export async function getDriverPortalSafetyStats(driverIdOverride?: string): Promise<SafetyStatsResponse> {
  return request<SafetyStatsResponse>(base(), '/v1/driver-portal/me/safety-stats', {
    method: 'GET',
    query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
  });
}

// Training
export interface TrainingCompletionItem {
  id: string;
  driverId: string;
  courseCode: string;
  completedAt: string;
  expiresAt: string | null;
}

export interface TrainingStatusResponse {
  completions: TrainingCompletionItem[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  total: number;
}

export async function getDriverPortalTraining(driverIdOverride?: string): Promise<TrainingStatusResponse> {
  return request<TrainingStatusResponse>(base(), '/v1/driver-portal/me/training', {
    method: 'GET',
    query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
  });
}

// ---------------------------------------------------------------------------
// Driver advisories — operational notices feed (driver-self-view + dismiss)
// ---------------------------------------------------------------------------

export type DriverAdvisoryKind = 'WEATHER' | 'ROUTE' | 'KPI' | 'GENERAL';
export type DriverAdvisorySeverity = 'INFO' | 'WARNING' | 'CRITICAL';

export interface DriverAdvisory {
  id: string;
  kind: DriverAdvisoryKind | string;
  severity: DriverAdvisorySeverity | string;
  title: string;
  body: string;
  targetDriverId: string | null;
  targetOutletCode: string | null;
  expiresAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  dismissed?: boolean;
}

export interface DriverAdvisoriesResponse {
  items: DriverAdvisory[];
}

export async function listMyDriverAdvisories(driverIdOverride?: string): Promise<DriverAdvisoriesResponse> {
  return request<DriverAdvisoriesResponse>(base(), '/v1/driver-portal/me/advisories', {
    method: 'GET',
    query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
  });
}

export async function dismissDriverAdvisory(
  advisoryId: string,
  driverIdOverride?: string,
): Promise<DriverAdvisory> {
  return request<DriverAdvisory>(
    base(),
    `/v1/driver-portal/me/advisories/${encodeURIComponent(advisoryId)}/dismiss`,
    {
      method: 'POST',
      query: driverIdOverride ? { driverId: driverIdOverride } : undefined,
    },
  );
}

// ---------------------------------------------------------------------------
// Driver advisories — admin / dispatcher CRUD (used by ops / e2e seeds)
// ---------------------------------------------------------------------------

export interface CreateDriverAdvisoryRequest {
  kind: DriverAdvisoryKind;
  severity: DriverAdvisorySeverity;
  title: string;
  body: string;
  targetDriverId?: string;
  targetOutletCode?: string;
  expiresAt?: string;
}

export async function createDriverAdvisory(body: CreateDriverAdvisoryRequest): Promise<DriverAdvisory> {
  return request<DriverAdvisory>(base(), '/v1/driver-advisories', {
    method: 'POST',
    body,
  });
}
